import { useEffect, useRef, useState } from "react";
import { FileText, Upload, Trash2, Download } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

const fmt = (iso) => { try { return format(parseISO(iso), "d MMM yyyy HH:mm"); } catch { return iso; } };

export const LeagueRules = ({ league, isAdmin }) => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/files/document/league/${league.id}`); setDocs(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [league.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/files/document/league/${league.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("League rules uploaded");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.title}"?`)) return;
    try { await api.delete(`/files/document/${d.id}`); toast.success("Removed"); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const openDoc = (d) => {
    const token = localStorage.getItem("access_token") || "";
    window.open(`${BACKEND_URL}/api/files/${d.storage_path}`, "_blank", "noopener");
  };

  const latest = docs[0];
  if (!isAdmin && docs.length === 0) return null;

  return (
    <div className="mt-4 border-t border-border pt-3" data-testid={`league-rules-${league.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">League rules</div>
        {isAdmin && (
          <>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid={`league-rules-file-${league.id}`} />
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`league-rules-upload-${league.id}`}
              className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1 disabled:opacity-50">
              <Upload className="w-3 h-3" /> {busy ? "Uploading…" : docs.length ? "Upload new version" : "Upload rules"}
            </button>
          </>
        )}
      </div>
      {docs.length === 0 ? (
        <div className="text-xs opacity-50 mt-1" data-testid={`league-rules-empty-${league.id}`}>No rules uploaded yet.</div>
      ) : (
        <>
          <div className="text-[11px] opacity-70 mt-1" data-testid={`league-rules-meta-${league.id}`}>
            Last updated {fmt(latest.created_at)}{latest.uploaded_by_name ? ` by ${latest.uploaded_by_name}` : ""}
          </div>
          <div className="mt-2 space-y-1">
            {docs.map((d, i) => (
              <div key={d.id} className="flex items-center gap-2 text-xs" data-testid={`league-rules-doc-${d.id}`}>
                <FileText className="w-3.5 h-3.5 text-[#83B68B] shrink-0" />
                <button type="button" onClick={() => openDoc(d)} className="hover:text-[#83B68B] hover:underline text-left truncate" data-testid={`league-rules-open-${d.id}`}>{d.title}</button>
                {i === 0 && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-[#83B68B]/15 text-[#83B68B]">Current</span>}
                <span className="opacity-50 whitespace-nowrap">{fmt(d.created_at)}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}</span>
                <button type="button" onClick={() => openDoc(d)} title="Open" className="ml-auto p-1 rounded hover:text-[#83B68B]"><Download className="w-3 h-3" /></button>
                {isAdmin && <button type="button" onClick={() => remove(d)} title="Remove" className="p-1 rounded hover:text-red-400" data-testid={`league-rules-delete-${d.id}`}><Trash2 className="w-3 h-3" /></button>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
